import { useCallback, useEffect, useState } from 'react';
import { UseFieldArrayReturn, useFormContext } from 'react-hook-form';
import { useDeliveryOverrideForNewTicketGroup } from 'src/hooks/api/useDeliveryOverrideForNewTicketGroup';
import { useBasicDialog } from 'src/hooks/useBasicDialog';
import { SpreadsheetTable } from 'src/tables/SpreadsheetTable/SpreadsheetTable';
import { ContentId } from 'src/utils/constants/contentId';
import { getInHandDateBasedOnEvent } from 'src/utils/eventWithDataUtils';
import { posChangedField } from 'src/utils/posFieldUtils';
import { PurchaseTicketsInput } from 'src/utils/purchaseUtils';
import { ListingNote } from 'src/WebApiController';

import { PurchaseOrderInfoForTicketGroupInput } from '../../PurchaseTicketsFlow/TicketGroupInput.types';
import { useTicketGroupInputTableColumns } from './hooks/useTicketGroupInputTableColumns';
import { SeatRangeInputModal } from './SeatRangeInputModal';
import * as styles from './TicketGroupInputTable.css';
import { TicketGroupInputTableFooter } from './TicketGroupInputTableFooter';
import { TicketGroupInputTableHeader } from './TicketGroupInputTableHeader';

export const TicketGroupInputTable = ({
  purchaseTicketGroupInputField,
  valueOptionsContent,
  listingNotesMap,
  purchaseOrderInfo,
  currencyCode,
  hideValueColumns = false,
  alwaysUseTotalCost = false,
}: {
  purchaseTicketGroupInputField: UseFieldArrayReturn<
    PurchaseTicketsInput,
    'ticketGroups',
    'id'
  >;
  valueOptionsContent: Record<string, ContentId | string>;
  listingNotesMap?: Record<string, ListingNote> | null;
  purchaseOrderInfo: PurchaseOrderInfoForTicketGroupInput | null | undefined;
  currencyCode: string;
  /** Hides the Face Value and List Price columns (used by the placeholder event group flow). */
  hideValueColumns?: boolean;
  /** Forces the total-cost column on and hides the toggle (placeholder event group flow). */
  alwaysUseTotalCost?: boolean;
}) => {
  const [isTotalCostCheckedState, setIsTotalCostChecked] = useState(false);
  const isTotalCostChecked = alwaysUseTotalCost || isTotalCostCheckedState;
  const [selectedSeatRangeRowIndex, setSelectedSeatRangeRowIndex] = useState<
    number | null
  >(null);

  const { fields, append } = purchaseTicketGroupInputField;
  const { setValue, getValues, getFieldState, formState } =
    useFormContext<PurchaseTicketsInput>();
  const seatRangeDialog = useBasicDialog();

  const launchSeatRangeDialog = useCallback((rowIndex: number) => {
    setSelectedSeatRangeRowIndex(rowIndex);
    seatRangeDialog.launchDialog();
  }, []);

  const ticketGroupColumnConfigs = useTicketGroupInputTableColumns({
    isTotalCostChecked,
    purchaseTicketGroupInputField,
    valueOptionsContent,
    listingNotesMap,
    launchSeatRangeDialog: launchSeatRangeDialog,
    currencyCode: currencyCode,
    poId: purchaseOrderInfo?.id ?? -1,
    hideValueColumns,
  });

  const options = {
    data: fields,
    columns: ticketGroupColumnConfigs,
  };

  const {
    isLoading: deliveryOverrideDefaultIsLoading,
    hasDefaultValues: hasDeliveryOverrideValues,
    deliveryTypeDefault,
    ticketTypeRulesDefault,
    inHandAtDefault,
  } = useDeliveryOverrideForNewTicketGroup(
    fields[0].event?.viagId,
    purchaseOrderInfo?.vendor?.id,
    purchaseOrderInfo?.vendAcc?.id,
    true, // isManual
    fields[0].ticketGroupId > 0 /* disabled if not new ticket group */
  );

  // Used for setting value when the default value is loaded
  useEffect(() => {
    if (!deliveryOverrideDefaultIsLoading) {
      fields.forEach((_, number) => {
        const inHandAt = getValues(`ticketGroups.${number}.inHandDate.value`);
        const delivType = getValues(
          `ticketGroups.${number}.deliveryType.value`
        );
        const desiredDelivType = deliveryTypeDefault ?? delivType;
        const ticketTypeOverrideField = getValues(
          `ticketGroups.${number}.ticketTypeRules`
        );
        if (deliveryTypeDefault && delivType !== deliveryTypeDefault) {
          setValue(
            `ticketGroups.${number}.deliveryType`,
            posChangedField(deliveryTypeDefault)
          );
        }

        if (inHandAtDefault && inHandAt !== inHandAtDefault) {
          setValue(
            `ticketGroups.${number}.inHandDate`,
            posChangedField(inHandAtDefault)
          );
        }

        if (ticketTypeRulesDefault) {
          setValue(`ticketGroups.${number}.ticketTypeRules`, {
            overrides: ticketTypeRulesDefault.overrides.filter(
              (o) => o.deliveryType === desiredDelivType
            ),
          });
        }
      });
    }
  }, [
    fields,
    deliveryOverrideDefaultIsLoading,
    deliveryTypeDefault,
    getValues,
    inHandAtDefault,
    setValue,
    ticketTypeRulesDefault,
  ]);

  useEffect(() => {
    const daysBeforeEvent = getValues('daysBeforeEvent');
    fields.forEach((tg, number) => {
      const inHandAt = getValues(`ticketGroups.${number}.inHandDate.value`);

      if (!inHandAt && tg.event?.dates?.start) {
        const inHandDate = getInHandDateBasedOnEvent(tg.event, daysBeforeEvent);
        setValue(
          `ticketGroups.${number}.inHandDate`,
          posChangedField(inHandDate)
        );
      }
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields]);

  return (
    <div className={styles.tableBase}>
      <TicketGroupInputTableHeader
        isTotalCostChecked={isTotalCostChecked}
        setIsTotalCostChecked={setIsTotalCostChecked}
        hideToggle={alwaysUseTotalCost}
      />
      <SpreadsheetTable
        options={options}
        nonInteractiveColumns={['actions']}
        rowHeight={40}
        fieldName="ticketGroups"
        getFieldState={getFieldState}
        formState={formState}
      />
      {seatRangeDialog.dialogProps.isOpen && (
        <SeatRangeInputModal
          {...seatRangeDialog.dialogProps}
          rowIndex={selectedSeatRangeRowIndex}
          onClosed={seatRangeDialog.closeDialog}
        />
      )}
      <TicketGroupInputTableFooter
        append={append}
        poId={purchaseOrderInfo?.id}
        deliveryOverrideDefaultIsLoading={deliveryOverrideDefaultIsLoading}
      />
    </div>
  );
};
